import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { CHAT_TAIL, DEFAULT_CHAT_SETTINGS, mergeChatMessages, type ChatEvent, type ChatMessage, type ChatSettings } from '@zip2live/shared/chat';
import { ApiError } from './api.ts';
import { ensureSession } from './auth.ts';

/**
 * Live chat state per stream. Fed by the stream's WebSocket (useStreamStatus routes chat events
 * here), sent through the API. A message you send shows at once as "sending", then is replaced
 * by the server's copy (which also arrives over the socket; seq makes the two one).
 */
export interface ShownMessage extends ChatMessage {
  /** Local echo of a message being sent. */
  pending?: boolean;
  clientId?: string;
}

export interface ChatState {
  messages: ShownMessage[];
  settings: ChatSettings;
  /** The room's recent chat has arrived. */
  ready: boolean;
  /** There may be older messages to load. */
  hasOlder: boolean;
  connected: boolean;
  /** The whole chat is loaded (a replay): nothing is trimmed. */
  full: boolean;
}

/** Kept in memory per page; older ones can be loaded again. */
const KEEP = 500;
const EMPTY: ChatState = { messages: [], settings: DEFAULT_CHAT_SETTINGS, ready: false, hasOlder: false, connected: false, full: false };
const states = new Map<string, ChatState>();
const listeners = new Map<string, Set<() => void>>();

const get = (id: string) => states.get(id) ?? EMPTY;
function set(id: string, next: ChatState) {
  states.set(id, next);
  for (const l of listeners.get(id) ?? []) l();
}

const confirmed = (s: ChatState) => s.messages.filter((m) => !m.pending);
const pending = (s: ChatState) => s.messages.filter((m) => m.pending);

function withConfirmed(s: ChatState, list: ChatMessage[], hasOlder = s.hasOlder): ChatState {
  // A pending echo disappears once its message is confirmed.
  if (s.full) return { ...s, messages: [...list, ...pending(s)], hasOlder: false };
  const trimmed = list.length > KEEP ? list.slice(list.length - KEEP) : list;
  return { ...s, messages: [...trimmed, ...pending(s)], hasOlder: hasOlder || list.length > KEEP };
}

/** An event pushed over the stream's socket. */
export function chatEvent(id: string, ev: ChatEvent) {
  const s = get(id);
  switch (ev.type) {
    case 'chat.tail': {
      // (Re)connected: continue the list if it overlaps, otherwise start again from the tail.
      const mine = confirmed(s);
      const last = mine.at(-1)?.seq ?? 0;
      const first = ev.messages[0]?.seq ?? Infinity;
      const continues = mine.length > 0 && first <= last + 1;
      const list = continues ? mergeChatMessages(mine, ev.messages) : ev.messages;
      set(id, { ...withConfirmed(s, list, continues ? s.hasOlder : ev.messages.length >= CHAT_TAIL), settings: ev.settings, ready: true });
      return;
    }
    case 'chat': {
      // Your own message can arrive here before its send returns: drop the matching echo now.
      const texts = new Set(ev.messages.map((m) => m.text));
      const next = withConfirmed(s, mergeChatMessages(confirmed(s), ev.messages));
      set(id, { ...next, messages: next.messages.filter((m) => !(m.pending && texts.has(m.text))) });
      return;
    }
    case 'chat.removed': {
      const gone = new Set(ev.seqs);
      const pinned = s.settings.pinned && gone.has(s.settings.pinned.seq) ? null : s.settings.pinned;
      set(id, { ...s, messages: s.messages.filter((m) => m.pending || !gone.has(m.seq)), settings: { ...s.settings, pinned } });
      return;
    }
    case 'chat.settings':
      set(id, { ...s, settings: ev.settings });
  }
}

export function setChatConnected(id: string, connected: boolean) {
  const s = get(id);
  if (s.connected !== connected) set(id, { ...s, connected });
}

export function useChat(id: string): ChatState {
  const subscribe = useCallback(
    (cb: () => void) => {
      let ls = listeners.get(id);
      if (!ls) listeners.set(id, (ls = new Set()));
      ls.add(cb);
      return () => void ls.delete(cb);
    },
    [id],
  );
  return useSyncExternalStore(subscribe, () => get(id));
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/v1${path}`, { credentials: 'include', ...init, headers: { 'Content-Type': 'application/json', ...init.headers } });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (body as { error?: { code: string; message: string; retryAfter?: number } } | null)?.error;
    throw Object.assign(new ApiError(res.status, err?.code ?? 'error', err?.message ?? 'Something went wrong. Try again.'), { retryAfter: err?.retryAfter });
  }
  return body as T;
}

export type ChatError = ApiError & { retryAfter?: number };

/**
 * Send a message. Network failures are retried with the same client id (the server keeps one
 * copy); a refusal (slow mode, a ban…) throws with the server's message.
 */
export async function sendChat(id: string, text: string, me: { name: string; badge: ChatMessage['author']['badge'] }, replyTo?: ChatMessage | null) {
  await ensureSession();
  const clientId = crypto.randomUUID();
  const echo: ShownMessage = {
    seq: Number.MAX_SAFE_INTEGER, at: Date.now(), pending: true, clientId,
    author: { key: 'me', name: me.name, badge: me.badge }, text,
    reply: replyTo ? { seq: replyTo.seq, name: replyTo.author.name, text: replyTo.text } : null,
  };
  set(id, { ...get(id), messages: [...get(id).messages, echo] });
  const drop = () => set(id, { ...get(id), messages: get(id).messages.filter((m) => m.clientId !== clientId) });
  for (let attempt = 0; ; attempt++) {
    try {
      const { message } = await call<{ message: ChatMessage }>(`/streams/${encodeURIComponent(id)}/chat`, {
        method: 'POST',
        body: JSON.stringify({ text, clientId, replyTo: replyTo?.seq }),
      });
      drop();
      myAuthorKeys.add(message.author.key);
      // The server's badge says you became (or stopped being) a mod: refresh your tools.
      if (message.author.badge !== (identities.get(id)?.badge ?? null)) void refreshChatIdentity(id);
      const s = get(id);
      set(id, withConfirmed(s, mergeChatMessages(confirmed(s), [message])));
      return message;
    } catch (err) {
      if (err instanceof ApiError || attempt >= 2) {
        drop();
        throw err instanceof ApiError ? err : new ApiError(0, 'network', 'Not sent. Check your connection and try again.');
      }
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
}

export async function loadOlder(id: string) {
  const s = get(id);
  const oldest = confirmed(s)[0]?.seq;
  const page = await call<{ messages: ChatMessage[] }>(`/streams/${encodeURIComponent(id)}/chat?limit=${CHAT_TAIL}${oldest ? `&before=${oldest}` : ''}`);
  const now = get(id);
  set(id, { ...withConfirmed(now, mergeChatMessages(page.messages, confirmed(now))), hasOlder: page.messages.length >= CHAT_TAIL });
}

/** A replay plays the chat back with the video, so it needs all of it: load forward from the start. */
const wholeLoads = new Map<string, Promise<void>>();
export function loadWholeChat(id: string) {
  let p = wholeLoads.get(id);
  if (!p) {
    p = (async () => {
      let all: ChatMessage[] = [];
      for (let after = 0, pages = 0; pages < 40; pages++) {
        const page = await call<{ messages: ChatMessage[] }>(`/streams/${encodeURIComponent(id)}/chat?after=${after}&limit=500`);
        all = all.concat(page.messages);
        if (page.messages.length < 500) break;
        after = page.messages.at(-1)!.seq;
      }
      const s = get(id);
      set(id, { ...s, full: true, ready: true, hasOlder: false, messages: [...mergeChatMessages(confirmed(s), all), ...pending(s)] });
    })().catch(() => void wholeLoads.delete(id));
    wholeLoads.set(id, p);
  }
  return p;
}

/** Host tools. The room pushes the result to everyone (including this page). */
export const chatHost = {
  remove: (id: string, seq: number) => call(`/streams/${id}/chat/${seq}`, { method: 'DELETE' }),
  timeout: (id: string, seq: number) => call(`/streams/${id}/chat/${seq}/timeout`, { method: 'POST' }),
  ban: (id: string, seq: number) => call(`/streams/${id}/chat/${seq}/ban`, { method: 'POST' }),
  pin: (id: string, seq: number | null) =>
    seq === null ? call(`/streams/${id}/chat/pin`, { method: 'DELETE' }) : call(`/streams/${id}/chat/pin`, { method: 'PUT', body: JSON.stringify({ seq }) }),
  settings: (id: string, change: { mode?: ChatSettings['mode']; slowSeconds?: number }) =>
    call<ChatSettings>(`/streams/${id}/chat/settings`, { method: 'PATCH', body: JSON.stringify(change) }),
  /** Host only: make the author of a message a mod (they need an account with an @handle), or not. */
  makeMod: (id: string, seq: number) => call<{ handle: string }>(`/streams/${id}/chat/${seq}/mod`, { method: 'POST' }),
  removeMod: (id: string, seq: number) => call(`/streams/${id}/chat/${seq}/mod`, { method: 'DELETE' }),
};

/** Your chat moderators, on all your streams. */
export const chatMods = {
  list: () => call<{ mods: { handle: string; createdAt: number }[] }>('/me/chat/mods'),
  add: (handle: string) => call<{ handle: string }>('/me/chat/mods', { method: 'POST', body: JSON.stringify({ handle }) }),
  remove: (handle: string) => call(`/me/chat/mods/${encodeURIComponent(handle)}`, { method: 'DELETE' }),
};

export const reportChat = (id: string, seq: number, reason: string) =>
  call(`/streams/${id}/chat/${seq}/report`, { method: 'POST', body: JSON.stringify({ reason }) });

export const chatBans = {
  list: () => call<{ bans: { key: string; name: string; createdAt: number }[] }>('/me/chat/bans'),
  lift: (key: string) => call(`/me/chat/bans/${encodeURIComponent(key)}`, { method: 'DELETE' }),
};

// ---- How you show in a stream's chat ----

export interface ChatIdentity {
  /** null: pick a name before chatting. */
  name: string | null;
  /** The name is your @handle. */
  locked: boolean;
  badge: ChatMessage['author']['badge'];
  /** You own this stream: host tools. */
  host: boolean;
  /** The creator made you a mod: the chat tools, but not managing mods. */
  mod: boolean;
}
const identities = new Map<string, ChatIdentity>();
const identityListeners = new Set<() => void>();
const loading = new Set<string>();
/** Keys of messages you sent, so your own lines get no "hide" and no mention highlight. */
export const myAuthorKeys = new Set<string>();

export async function refreshChatIdentity(streamId: string) {
  loading.add(streamId);
  const who = await call<ChatIdentity>(`/me/chat?stream=${encodeURIComponent(streamId)}`).catch(() => ({ name: null, locked: false, badge: null, host: false, mod: false }));
  identities.set(streamId, who);
  for (const l of identityListeners) l();
}

export function useChatIdentity(streamId: string): ChatIdentity | null {
  const who = useSyncExternalStore(
    (cb) => {
      identityListeners.add(cb);
      return () => void identityListeners.delete(cb);
    },
    () => identities.get(streamId) ?? null,
  );
  useEffect(() => {
    if (!identities.has(streamId) && !loading.has(streamId)) void refreshChatIdentity(streamId);
    // Made a mod (or not any more) while watching: pick it up when the page comes back into view.
    const onFocus = () => void refreshChatIdentity(streamId);
    addEventListener('focus', onFocus);
    return () => removeEventListener('focus', onFocus);
  }, [streamId]);
  return who;
}

export async function saveChatName(streamId: string, name: string) {
  await ensureSession();
  await call('/me/chat/name', { method: 'PUT', body: JSON.stringify({ name }) });
  await refreshChatIdentity(streamId);
}

// ---- People you hid (this device only, from before blocking) and people you blocked (your account) ----

const HIDDEN_KEY = 'z2l:chat:hidden';
let hidden: Set<string> = (() => {
  try {
    return new Set<string>(JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '[]'));
  } catch {
    return new Set<string>();
  }
})();
const hiddenListeners = new Set<() => void>();
let blocked = new Set<string>();
/** Hidden and blocked together: what the chat leaves out. */
let leftOut: Set<string> = hidden;

function changed() {
  leftOut = blocked.size ? new Set([...hidden, ...blocked]) : hidden;
  for (const l of hiddenListeners) l();
}

export function hideAuthor(key: string, on = true) {
  hidden = new Set(hidden);
  if (on) hidden.add(key);
  else hidden.delete(key);
  try {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify([...hidden].slice(-500)));
  } catch {
    /* private mode */
  }
  changed();
}

/** The chat keys of people you blocked (lib/safety.ts keeps this up to date). */
export function setBlockedAuthors(keys: Iterable<string>) {
  blocked = new Set(keys);
  changed();
}

export function useHiddenAuthors() {
  return useSyncExternalStore(
    (cb) => {
      hiddenListeners.add(cb);
      return () => void hiddenListeners.delete(cb);
    },
    () => leftOut,
  );
}
